import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacidadPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>

        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Política de Privacidad</h1>
          <p className="text-slate-500 mb-8">Última actualización: Marzo 2025</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Introducción</h2>
              <p className="text-slate-600 mb-4">
                En KINOSIS nos comprometemos a proteger la privacidad de nuestros usuarios y de las personas cuyos datos son gestionados a través de nuestra plataforma. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos su información personal.
              </p>
              <p className="text-slate-600 mb-4">
                Al utilizar nuestro Servicio, usted acepta las prácticas descritas en esta política. Le recomendamos leerla detenidamente.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Información que Recopilamos</h2>
              
              <h3 className="text-lg font-medium text-slate-800 mb-3">2.1 Información de Administradores de Cuenta</h3>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Nombre completo</li>
                <li>Dirección de correo electrónico</li>
                <li>Información de autenticación (Google OAuth)</li>
                <li>Datos de facturación (para cuentas de pago)</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mb-3">2.2 Información de Integrantes (ingresada por administradores)</h3>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Nombre y apellidos</li>
                <li>RUT o documento de identidad</li>
                <li>Fecha de nacimiento</li>
                <li>Correo electrónico y teléfono de contacto</li>
                <li>Dirección (opcional)</li>
                <li>Fotografía (opcional)</li>
                <li>Información deportiva y de membresía</li>
              </ul>

              <h3 className="text-lg font-medium text-slate-800 mb-3">2.3 Información de Uso</h3>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Registros de acceso y actividad</li>
                <li>Dirección IP y tipo de dispositivo</li>
                <li>Navegador utilizado</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Uso de la Información</h2>
              <p className="text-slate-600 mb-4">
                Utilizamos la información recopilada para:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Proporcionar, mantener y mejorar nuestro Servicio</li>
                <li>Procesar transacciones y enviar notificaciones relacionadas</li>
                <li>Responder a consultas y brindar soporte técnico</li>
                <li>Enviar comunicaciones importantes sobre el Servicio</li>
                <li>Detectar, prevenir y abordar problemas técnicos o de seguridad</li>
                <li>Cumplir con obligaciones legales</li>
              </ul>
            </section>

            <section className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-blue-900 mb-4">4. Compromiso de No Compartir Datos con Terceros</h2>
              <p className="text-blue-800 mb-4">
                <strong>KINOSIS NO vende, alquila ni comparte la información personal de sus usuarios ni de los integrantes registrados con terceros para fines comerciales o de marketing sin su consentimiento expreso.</strong>
              </p>
              <p className="text-blue-800 mb-4">
                La información de los integrantes de su club, academia o liga es confidencial y únicamente accesible por los administradores y usuarios autorizados de su organización.
              </p>
              <p className="text-blue-800">
                Solo compartiremos información en los siguientes casos excepcionales:
              </p>
              <ul className="list-disc list-inside text-blue-800 mt-2 ml-4">
                <li>Cuando sea requerido por ley o autoridad competente</li>
                <li>Para proteger los derechos, propiedad o seguridad de KINOSIS o sus usuarios</li>
                <li>Con su consentimiento expreso</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Comunicaciones Comerciales Deportivas</h2>
              <p className="text-slate-600 mb-4">
                Con su consentimiento expreso, podremos enviarle comunicaciones comerciales <strong>exclusivamente relacionadas con el ámbito deportivo</strong>, incluyendo:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Seguros deportivos y coberturas para deportistas</li>
                <li>Equipamiento e indumentaria deportiva</li>
                <li>Suplementación y nutrición deportiva</li>
                <li>Promociones y descuentos en planes de KINOSIS</li>
                <li>Novedades y nuevas funcionalidades del servicio</li>
                <li>Eventos deportivos y competencias</li>
              </ul>
              <p className="text-slate-600 mb-4">
                <strong>No enviaremos publicidad no relacionada con el deporte.</strong> Este consentimiento es opcional y puede revocarlo en cualquier momento desde la configuración de su cuenta o contactándonos directamente. La negativa a recibir comunicaciones comerciales no afectará su acceso al Servicio.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Seguridad de los Datos</h2>
              <p className="text-slate-600 mb-4">
                Implementamos medidas de seguridad técnicas y organizativas para proteger su información, incluyendo:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Encriptación de datos en tránsito (HTTPS/SSL)</li>
                <li>Acceso restringido a la información personal</li>
                <li>Monitoreo de seguridad continuo</li>
                <li>Copias de seguridad periódicas</li>
              </ul>
              <p className="text-slate-600 mb-4">
                Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro. Hacemos nuestro mejor esfuerzo para proteger su información, pero no podemos garantizar su seguridad absoluta.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Retención de Datos</h2>
              <p className="text-slate-600 mb-4">
                Conservamos su información personal mientras su cuenta esté activa o según sea necesario para proporcionarle el Servicio. Si solicita la eliminación de su cuenta, eliminaremos o anonimizaremos su información en un plazo de 30 días, salvo que debamos conservarla por obligaciones legales.
              </p>
              <p className="text-slate-600 mb-4">
                Para cuentas gratuitas inactivas, los datos pueden ser eliminados después de 12 meses de inactividad, previa notificación.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Sus Derechos</h2>
              <p className="text-slate-600 mb-4">
                Usted tiene derecho a:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li><strong>Acceso:</strong> Solicitar una copia de la información personal que tenemos sobre usted</li>
                <li><strong>Rectificación:</strong> Corregir información inexacta o incompleta</li>
                <li><strong>Eliminación:</strong> Solicitar la eliminación de su información personal</li>
                <li><strong>Portabilidad:</strong> Recibir sus datos en un formato estructurado y legible</li>
                <li><strong>Oposición:</strong> Oponerse al procesamiento de sus datos en ciertas circunstancias</li>
              </ul>
              <p className="text-slate-600 mb-4">
                Para ejercer estos derechos, contáctenos a través de soporte@kinosis.cl
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Cookies y Tecnologías Similares</h2>
              <p className="text-slate-600 mb-4">
                Utilizamos cookies esenciales para el funcionamiento del Servicio, incluyendo:
              </p>
              <ul className="list-disc list-inside text-slate-600 mb-4 ml-4">
                <li>Cookies de sesión para mantener su inicio de sesión</li>
                <li>Cookies de preferencias para recordar su configuración</li>
              </ul>
              <p className="text-slate-600 mb-4">
                No utilizamos cookies de seguimiento ni compartimos datos de navegación con redes publicitarias.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Menores de Edad</h2>
              <p className="text-slate-600 mb-4">
                El Servicio puede contener información de menores de edad (integrantes de clubes deportivos). Esta información es ingresada y gestionada exclusivamente por los administradores autorizados de cada organización, quienes son responsables de obtener los consentimientos necesarios de los padres o tutores legales.
              </p>
              <p className="text-slate-600 mb-4">
                KINOSIS no recopila intencionalmente información de menores de 13 años sin el consentimiento verificable de sus padres o tutores.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Transferencias Internacionales</h2>
              <p className="text-slate-600 mb-4">
                Sus datos pueden ser procesados en servidores ubicados fuera de su país de residencia. Nos aseguramos de que cualquier transferencia internacional de datos cumpla con las leyes de protección de datos aplicables.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Cambios en esta Política</h2>
              <p className="text-slate-600 mb-4">
                Podemos actualizar esta Política de Privacidad ocasionalmente. Le notificaremos cualquier cambio significativo publicando la nueva política en nuestro Servicio y, cuando sea apropiado, enviándole un aviso por correo electrónico.
              </p>
              <p className="text-slate-600 mb-4">
                Le recomendamos revisar esta política periódicamente para mantenerse informado sobre cómo protegemos su información.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">13. Contacto</h2>
              <p className="text-slate-600 mb-4">
                Si tiene preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad o el tratamiento de sus datos personales, puede contactarnos:
              </p>
              <p className="text-slate-600">
                <strong>Email:</strong> soporte@kinosis.cl
              </p>
              <p className="text-slate-600 mt-2">
                <strong>Responsable del Tratamiento:</strong> KINOSIS
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
